<template>
    <div>

        <PageHeader>
            {{ $t('newInvoice') }}
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card>

            <b-form @submit.prevent>

                <FormValidator :label="$t('advertiser')" form-group v-slot="slotProps" :validator="$v.form.advertiser" label-cols-sm="3">
                    <div v-if="$route.params.item || $route.params.items" class="form-control" readonly>
                        {{ mapAdvertiser(advertiser).optionName }}
                    </div>
                    <Multiselect
                        v-else
                        show-selected
                        asynchronous
                        :fetch-options-function="fetchAdvertisers"
                        track-by="id"
                        label="optionName"
                        select-label=""
                        :state="slotProps.state"
                        :disabled="submitting"
                        :track-by-label="$t('id')"
                        @changeTrackBy="form.advertiser = $event"
                    />
                </FormValidator>

                <FormValidator :label="$t('issueDate')" form-group v-slot="slotProps" :validator="$v.form.createdAt" label-cols-sm="3">
                    <FormDatepicker v-model="form.createdAt" :disabled="submitting" :state="slotProps.state" />
                </FormValidator>

                <FormValidator :label="$t('dueDate')" form-group v-slot="slotProps" :validator="$v.form.dueDate" label-cols-sm="3">
                    <b-input-group>
                        <b-form-input v-model.number="form.dueDate" :disabled="submitting" :state="slotProps.state" />
                        <b-input-group-append>
                            <b-input-group-text>{{ $t('days').toLowerCase() }}</b-input-group-text>
                        </b-input-group-append>
                    </b-input-group>
                </FormValidator>

                <FormValidator :label="$t('invoiceNumber')" form-group v-slot="slotProps" :validator="$v.form.number" label-cols-sm="3">
                    <b-form-input v-model="form.number" :disabled="submitting" :state="slotProps.state" />
                </FormValidator>

                <FormValidator :label="$t('pdf')" form-group v-slot="slotProps" :validator="$v.form.pdf" label-cols-sm="3">
                    <FormFile
                        v-model="form.pdf"
                        :disabled="submitting"
                        :state="slotProps.state"
                        accept=".pdf"
                        :browse-text="$t('add')"
                        :placeholder="$t('fileField.placeholder')"
                    />
                </FormValidator>

                <div class="d-flex flex-column gap-1">
                    <b-table-simple class="mb-0">
                        <b-thead>
                            <b-tr class="border-bottom">
                                <b-th class="border-top-0 py-1 w-50 pl-0">{{ $t('itemsCampaigns') }}</b-th>
                                <b-th class="border-top-0 py-1 text-right">{{ $t('netSettlementAmount') }}</b-th>
                                <b-th class="border-top-0 py-1 text-right">{{ $t('netInvoiceAmount') }}</b-th>
                                <b-th class="border-top-0 py-1 width-10 pr-0" />
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <b-tr v-for="(item, key) in form.items" :key="key">
                                <b-td class="py-1 pl-0">
                                    <div class="d-flex align-items-center gap-2">
                                        <div class="form-control w-auto p-0 border-0 d-flex align-items-center font-weight-bold">
                                            {{ key }}.
                                        </div>
                                        <FormValidator class="d-flex flex-column flex-grow-2 flex-shrink-0 flex-basis-0" :validator="$v.form.items[key].campaign" v-slot="slotProps">
                                            <Multiselect
                                                :ref="`campaign-${key}-${form.advertiser || 0}`"
                                                :key="`campaign-${key}-${form.advertiser || 0}`"
                                                :initial-value="form.items[key].campaign"
                                                :options="optionsCampaignSettlements"
                                                :loading="requestCampaignSettlements.getLoading()"
                                                track-by="id"
                                                label="optionName"
                                                select-label=""
                                                :state="slotProps.state"
                                                :disabled="submitting || !!advertiser"
                                                @change="setFormItem($event, key)"
                                            />
                                        </FormValidator>
                                    </div>
                                </b-td>
                                <b-td class="py-1">
                                    <div class="form-control w-auto p-0 border-0 d-flex align-items-center font-weight-bold justify-content-end">
                                        <template v-if="form.items[key].campaign">{{ $format.currency($refs[`campaign-${key}-${form.advertiser || 0}`]?.[0]?.modelValue.currentSettlement ?? form.items[key].settlement) }}</template>
                                    </div>
                                </b-td>
                                <b-td class="py-1">
                                    <FormValidator class="d-flex flex-column flex-grow-1 flex-shrink-0 flex-basis-0" :validator="$v.form.items[key].settlement" v-slot="slotProps">
                                        <b-input-group>
                                            <b-form-input v-model.number="form.items[key].settlement" :disabled="submitting" :state="slotProps.state" />
                                            <b-input-group-append>
                                                <b-input-group-text>{{ $currencySymbol }}</b-input-group-text>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </FormValidator>
                                </b-td>
                                <b-td class="py-1 pr-0">
                                    <ButtonIcon
                                        :disabled="submitting || (form.items.length < 2)"
                                        variant="danger"
                                        icon="trash-2"
                                        :title="$t('remove')"
                                        class="mb-auto"
                                        @click="removeItem(key)"
                                    />
                                </b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                    <ButtonIcon icon="plus" :disabled="submitting" variant="dark" class="wide mr-auto" @click="addItem()">
                        {{ $t('addAnotherItem') }}
                    </ButtonIcon>
                </div>

                <div class="my-5"></div>

                <div class="d-flex gap-3">
                    <ButtonIcon :loading="submitting" hide-label-while-loading :disabled="false" class="wide" @click="formSubmit()">
                        {{ $t('issueInvoice') }}
                    </ButtonIcon>
                </div>

            </b-form>

        </Card>

    </div>
</template>



<script>
    import { serialize as objectToFormData } from 'object-to-formdata';
    import { addDays, format } from 'date-fns';
    import { required, numeric, decimal } from 'vuelidate/lib/validators';
    import { scrollToElement } from '@/utils/util';
    import cloneDeep from 'lodash/cloneDeep';
    import Role from '@/Role';
    import userService from '@/services/userService';
    import formBackendValidationMixin from '@/mixins/formBackendValidationMixin';
    import FormValidator from '@/components/common/FormValidator.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import FormDatepicker from '@/components/common/FormDatepicker.vue';
    import FormFile from '@/components/common/FormFile.vue';

    const DATE_FORMAT = 'yyyy-MM-dd';

    const FORM_DEFAULT_DATA = Object.freeze({
        advertiser: null,
        createdAt: format(new Date(), DATE_FORMAT),
        dueDate: 14,
        number: '',
        pdf: null,
        items: [
            {
                campaign: null,
                settlement: null,
            },
        ],
    });

    export default {
        name: 'Create',
        mixins: [formBackendValidationMixin],
        components: {
            FormValidator,
            Multiselect,
            FormDatepicker,
            FormFile,
        },
        data() {
            return {
                request: this.$req('advertiserSettlements/ReportersInvoices'),
                requestCampaignSettlements: this.$req('advertiserSettlements/CampaignSettlements'),
                form: cloneDeep(FORM_DEFAULT_DATA),
            };
        },
        computed: {
            advertiser() {
                return this.$route.params.item?.advertiser || this.$route.params.items?.[0]?.advertiser || null;
            },
            formValidations() {
                return {
                    form: {
                        advertiser: { required: val => (this.advertiser ? true : required(val)) },
                        number: { required },
                        createdAt: { required },
                        dueDate: { required, numeric },
                        pdf: {},
                        items: Object.keys(this.form.items).reduce((acc, key) => {
                            const item = this.form.items[key];
                            return {
                                ...acc,
                                [key]: {
                                    campaign: {
                                        required: () => required(item.campaign),
                                    },
                                    settlement: {
                                        required: () => required(item.settlement),
                                        decimal: () => decimal(item.settlement),
                                    },
                                },
                            };
                        }, {}),
                    },
                };
            },
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            submitting() {
                return this.request.getLoading();
            },
            optionsCampaignSettlements() {
                let options = [];
                if (this.$route.params.item) {
                    options = [this.$route.params.item];
                } else if (this.$route.params.items) {
                    options = this.$route.params.items;
                } else {
                    options = this.requestCampaignSettlements.getData()?.items || [];
                }
                return options.map(this.mapCampaignSettlements);
            },
        },
        watch: {
            'form.advertiser': {
                handler(id) {
                    if (id) {
                        this.fetchCampaignSettlements();
                        this.form.items = cloneDeep(FORM_DEFAULT_DATA.items);
                    }
                },
            },
        },
        methods: {
            fetchAdvertisers({ search }) {
                const requestParams = { search, roles: [Role.ADVERTISER] };
                return userService.getUsers({ requestParams }).then(data => (data?.items || []).map(this.mapAdvertiser));
            },
            mapAdvertiser(item) {
                const name = item.name || `${item.profile.firstName} ${item.profile.lastName}`;
                return { ...item, optionName: `${name} (${item.username || item.company})` };
            },
            fetchCampaignSettlements() {
                return this.requestCampaignSettlements.get({ urlParams: { id: this.form.advertiser } });
            },
            mapCampaignSettlements(item) {
                return {
                    ...item,
                    id: item.campaign.id,
                    optionName: `${item.advertiser.name} | ${item.advertiser.company} | ${item.campaign.name} | ${this.$format.currency(item.currentSettlement)}`,
                };
            },
            async formSubmit() {
                this.$v.form.$touch();
                if (this.$v.form.$invalid) {
                    await this.$nextTick();
                    scrollToElement('.is-invalid', { offset: -80 });
                    return;
                }
                const form = { ...this.form };
                form.dueDate = format(addDays(this.form.createdAt, -Number.parseInt(this.form.dueDate)), DATE_FORMAT);
                delete form.advertiser;
                const apiParams = {
                    urlParams: { id: this.advertiser?.id || this.form.advertiser },
                    requestParams: objectToFormData(form, { indices: true }),
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
                this.request
                    .post(apiParams)
                    .then(() => {
                        this.$v.form.$reset();
                        this.$toastr.success(this.$t('dataSavedSuccessfully'));
                        this.$router.push({ name: 'SettlementsInvoices' });
                    })
                    .catch(this.handleBackendValidations);
            },
            addItem() {
                this.form.items.push({ ...FORM_DEFAULT_DATA.items[0] });
            },
            removeItem(index) {
                this.form.items.splice(index, 1);
            },
            setFormItem(item, index) {
                this.form.items[index].campaign = item?.campaign.id ?? null;
                this.form.items[index].settlement = item?.currentSettlement ?? null;
            },
            initFormData() {
                const items = this.$route.params.item ? [this.$route.params.item] : this.$route.params.items || [];
                items.forEach((item, index) => {
                    if (!this.form.items[index]) this.addItem();
                    this.setFormItem(item, index);
                });
            },
        },
        created() {
            this.initFormData();
        },
    };
</script>
